import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
// Session-free client: a stale login token in localStorage must never
// poison public reads (the careers/survey "empty page" bug class).
import { publicSupabase as supabase } from '../lib/supabase';

export default function Download() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleDownload = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('download-launcher');

      if (fnError) throw fnError;
      if (!data?.url) throw new Error('No download URL returned');

      // Open pre-signed URL to start download
      window.open(data.url, '_blank');
    } catch (err) {
      console.error('Download error:', err);
      setError(t('download.downloadFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {t('download.title')}
          </h1>
          <p className="mt-2 text-lg text-gray-600">
            {t('download.subtitle')}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        {/* Windows Platform Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-8">
            <div className="flex items-start gap-6">
              {/* Windows Icon */}
              <div className="flex-shrink-0 w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center">
                <svg className="w-9 h-9 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 12V6.75l8-1.25V12H3zm0 .5h8v6.5l-8-1.25V12.5zM11.5 12V5.35l9.5-1.6V12H11.5zm0 .5h9.5v8.25l-9.5-1.6V12.5z" />
                </svg>
              </div>

              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-semibold text-gray-900">
                  {t('download.windows')}
                </h2>
                <p className="mt-1 text-gray-500">
                  {t('download.windowsDesc')}
                </p>
                <p className="mt-1 text-sm text-gray-400">
                  {t('download.fileInfo')}
                </p>

                {/* Download Button */}
                <button
                  onClick={handleDownload}
                  disabled={loading}
                  className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-[#C41E3A] text-white font-medium rounded-lg hover:bg-[#8B0000] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      {t('download.downloading')}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                      </svg>
                      {t('download.downloadBtn')}
                    </>
                  )}
                </button>

                {/* Error message */}
                {error && (
                  <p className="mt-3 text-sm text-red-600">{error}</p>
                )}
              </div>
            </div>
          </div>

          {/* Instructions footer */}
          <div className="bg-gray-50 px-8 py-4 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              {t('download.instructions')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
